<script setup>
import { ref, onMounted, shallowRef, watch } from 'vue';
import CardUsersData from './components/CardUsersData.vue';
import Chart from '@components/global/Chart.vue';
import ChartBar from '@components/global/ChartBar.vue';
import Pie from '@components/global/Pie.vue';
import { nameFormat } from '@utils';
import { storeToRefs } from 'pinia';
import { useSessionStore } from '@stores';
import { useI18n } from 'vue-i18n';
import AsideRight from './components/AsideRight.vue';

const { t } = useI18n({ useScope: 'global' });
const sessionStore = useSessionStore();
const { topBlockedList, topCodeList, statsList, selectedSession, sessionsList, sessionsForSelect } = storeToRefs(sessionStore);

const switchWords = shallowRef([
    { word: t('chart.week'), id: 1 },
    { word: t('chart.month'), id: 2 },
    { word: t('chart.year'), id: 3 },
]);
const selectedDate = ref(switchWords.value[0]);

const getData = async () => {
    await sessionStore.getLastUser();
    await sessionStore.getCounters();
    await sessionStore.getTopBlocked();
    await sessionStore.getTopCode();
    await sessionStore.getStats(1);
    await sessionStore.getStatUsers();
};

onMounted(async () => {
    if (sessionsList) {
        await getData();
    }
});

watch(
    () => selectedDate.value,
    async (value) => {
        await sessionStore.getStats(value.id);
    }
);
</script>

<template>
    <div class="flex flex-col gap-4">
        <div class="dashboard-top">
            <div class="dashboard-left">
                <div class="counters">
                    <Card class="counters-card">
                        <template #title>
                            <Htag class="statTitle mb-4"> {{ t('global.performed') }}</Htag>
                        </template>
                        <template #content>
                            <CardUsersData :stat="sessionStore.counters.totalCount" />
                        </template>
                    </Card>
                    <Card class="counters-card">
                        <template #title>
                            <Htag class="statTitle mb-4">{{ t('global.conductedToday') }}</Htag>
                        </template>
                        <template #content>
                            <CardUsersData :stat="sessionStore.counters.countToday?.result" :percent="sessionStore.counters.countToday?.percent" />
                        </template>
                    </Card>
                    <Card class="counters-card">
                        <template #title>
                            <Htag class="statTitle mb-4"> {{ t('global.blockedToday') }}</Htag>
                        </template>
                        <template #content>
                            <CardUsersData :stat="sessionStore.counters.countTodayBlocked?.result" :percent="sessionStore.counters.countTodayBlocked?.percent" />
                        </template>
                    </Card>
                    <Card class="counters-card">
                        <template #title>
                            <Htag class="statTitle mb-4"> {{ t('global.completedToday') }}</Htag>
                        </template>
                        <template #content>
                            <CardUsersData :stat="sessionStore.counters.countTodayFinished?.result" :percent="sessionStore.counters.countTodayFinished?.percent" />
                        </template>
                    </Card>
                </div>

                <div class="charts">
                    <Card class="card-chart !col-span-3">
                        <template #title>
                            <Htag class="statTitle">
                                {{ t('chart.statistics') }}
                                <SelectButton v-model="selectedDate" :options="switchWords" optionLabel="word" class="word-switch" />
                            </Htag>
                        </template>

                        <template #content>
                            <Chart :data="statsList" />
                        </template>
                    </Card>
                    <Card class="card-chart col-start-4">
                        <template #title>
                            <Htag class="statTitle mb-4">{{ t('chart.topBlocked') }}</Htag>
                        </template>

                        <template #content>
                            <AvatarLine v-if="topBlockedList?.length" size="md" :name="nameFormat(full_name)" :time="`${count} ${count > 1 ? t('global.times') : t('global.time')}`" :src="photo" :key="id" class="blockedAvatar" :to="`/session/${sessionStore.selectedSession}/user/${id}`" v-for="{ full_name, photo, id, count } in topBlockedList" />
                            <span v-else class="text-gray-500 text-xs"> {{ t('chart.noData') }} </span>
                        </template>
                    </Card>
                    <Card class="card-chart col-span-2 row-start-2">
                        <template #title>
                            <Htag class="statTitle">{{ t('chart.userStatistics') }}</Htag>
                        </template>
                        <template #content>
                            <ChartBar :data="sessionStore.usersStat" />
                        </template>
                    </Card>
                    <Card class="card-chart col-span-2 col-start-3 row-start-2">
                        <template #title>
                            <Htag class="statTitle">{{ t('chart.topViolations') }}</Htag>
                        </template>
                        <template #content>
                            <div class="pie-wrapper">
                                <Pie :data="topCodeList" />
                            </div>
                        </template>
                    </Card>
                </div>
            </div>
            <AsideRight />
        </div>
    </div>
</template>

<style scoped>
.switch {
    @apply mt-[-8px] mb-2;
}

.blockedAvatar {
    @apply p-2;
}

.statTitle {
    @apply flex justify-between dark:text-[--main-white];
}

.dashboard-top {
    @apply flex justify-end  mb-4 flex-row gap-4;
}
.dashboard-left {
    @apply w-full;
}
.title {
    @apply mb-5 text-wrap dark:text-white;
}

.counters {
    @apply grid grid-cols-4 gap-7;
}

.charts {
    @apply grid grid-cols-4 grid-rows-[auto_auto] gap-4 mt-7;
}

.pie-wrapper {
    @apply flex items-center justify-between;
}

.pie-items {
    @apply flex justify-between;
}

.pie-item {
    @apply flex items-center gap-2;
}

.pie-dot {
    @apply w-2 h-2 rounded-full bg-black block;
}

:deep.word-switch .p-togglebutton-checked:before {
    @apply bg-black;
}

:deep.word-switch .p-togglebutton-checked span {
    @apply text-white;
}

@media (max-width: 640px) {
}

@media (max-width: 768px) {
    .counters {
        @apply flex flex-col gap-2;
    }
    .dashboard-top {
        @apply flex-col gap-2;
    }
    .charts {
        @apply flex flex-col gap-4;
    }
    .dashboard-left {
        @apply w-full;
    }
    .counters .p-card-body {
        @apply flex flex-row gap-2 items-center;
    }
}

@media (max-width: 1024px) {
}

@media (max-width: 1280px) {
}

@media (max-width: 1536px) {
}
</style>
